import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  User, MapPin, Phone, Send, Package,
  Calendar, Settings, Star, Truck,
} from 'lucide-react'
import { ProgressBar } from './ProgressBar'
import type { SolicitudListItem, ActualizarEstadoPayload, CampoDinamico } from '../types'

function EstadoBadge({
  valor,
  tipo,
}: {
  valor: string
  tipo: 'contacto' | 'envio' | 'entrega'
}) {
  const iconMap = {
    contacto: Phone,
    envio: Send,
    entrega: Package,
  }
  const Icon = iconMap[tipo]

  // Mapear labels de display
  const labelMap: Record<string, Record<string, string>> = {
    contacto: { Pendiente: 'Pendiente', Contactado: 'Contactado', Repetido: 'Repetido' },
    envio: { Pendiente: 'No Enviado', 'Envio programado': 'Programado', Enviado: 'Enviado', Repetido: 'Repetido' },
    entrega: { Pendiente: 'No Entregado', Entregado: 'Entregado', Repetido: 'Repetido' },
  }

  const label = labelMap[tipo][valor] ?? valor

  // Estilos según estado activo o pendiente
  const isActive = ['Contactado', 'Enviado', 'Entregado', 'Envio programado'].includes(valor)
  const badgeClass = isActive
    ? 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-700'
    : 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${badgeClass}`}>
      <Icon className="h-3 w-3 shrink-0" />
      {label}
    </span>
  )
}


interface SolicitudesTablaProps {
  solicitudes: SolicitudListItem[]
  camposDinamicos?: CampoDinamico[]
  onEstadoChange: (payload: ActualizarEstadoPayload) => void
  onRowClick: (solicitudId: number) => void
}

export function SolicitudesTabla({
  solicitudes,
  onEstadoChange: _onEstadoChange,
  onRowClick,
}: SolicitudesTablaProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center"><span className="flex items-center justify-center gap-1.5"><User className="h-3.5 w-3.5" />Persona</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center"><span className="flex items-center justify-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Ubicación</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center"><span className="flex items-center justify-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Registro</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center"><span className="flex items-center justify-center gap-1.5"><Phone className="h-3.5 w-3.5" />Contacto</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center"><span className="flex items-center justify-center gap-1.5"><Send className="h-3.5 w-3.5" />Envío</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center"><span className="flex items-center justify-center gap-1.5"><Package className="h-3.5 w-3.5" />Entrega</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center whitespace-nowrap"><span className="flex items-center justify-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Fecha Envío</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center w-[130px] whitespace-nowrap"><span className="flex items-center justify-center gap-1.5">% Progreso</span></TableHead>
            <TableHead className="uppercase text-xs font-semibold tracking-wider text-center"><span className="flex items-center justify-center gap-1.5"><Settings className="h-3.5 w-3.5" />Acciones</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {solicitudes.map((sol) => {
            const [fecha, hora] = sol.fecha_solicitud.split(' ')
            return (
              <TableRow
                key={sol.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onRowClick(sol.id)}
              >
                {/* Persona */}
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="font-medium whitespace-nowrap">{sol.nombre_completo}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 ml-[1.25rem]">
                    <Star className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground">{sol.responsable_nombre || 'Sin asignar'}</span>
                  </div>
                </TableCell>
                {/* Ubicación */}
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm whitespace-nowrap">{sol.ciudad || sol.provincia}</span>
                  </div>
                  {sol.ciudad && sol.provincia && (
                    <div className="text-xs text-muted-foreground ml-[1.25rem] mt-0.5">{sol.provincia}</div>
                  )}
                </TableCell>
                {/* Registro */}
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm whitespace-nowrap">{fecha}</span>
                  </div>
                  <div className="text-xs text-muted-foreground ml-[1.25rem] mt-0.5">{hora}</div>
                </TableCell>
                {/* Contacto */}
                <TableCell className="whitespace-nowrap text-center">
                  <EstadoBadge valor={sol.estado_contacto} tipo="contacto" />
                </TableCell>
                {/* Envío */}
                <TableCell className="whitespace-nowrap text-center">
                  <EstadoBadge valor={sol.estado_envio} tipo="envio" />
                </TableCell>
                {/* Entrega */}
                <TableCell className="whitespace-nowrap text-center">
                  <EstadoBadge valor={sol.estado_entrega} tipo="entrega" />
                </TableCell>
                {/* Fecha Envío */}
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm whitespace-nowrap">{sol.fecha_envio_valor || 'Sin fecha'}</span>
                  </div>
                </TableCell>
                {/* % Progreso */}
                <TableCell>
                  <ProgressBar porcentaje={sol.porcentaje_progreso} />
                </TableCell>
                {/* Acciones */}
                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => onRowClick(sol.id)}
                      className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      title="Ver detalle"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                    {sol.numero_guia && (
                      <a
                        href={`https://www.servientrega.com.ec/Tracking/?guia=${sol.numero_guia}&tipo=GUIA`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-green-600 dark:text-green-400"
                        title={`Rastrear envío: ${sol.numero_guia}`}
                      >
                        <Truck className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
